import {
  AfterViewInit, ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  InputSignal,
  Signal,
  ViewChild
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {Params} from '@angular/router';

import {createBracket, OptionsMap} from 'bracketry';

import {DrawingModel, GameModel, ScoreModel} from 'shared/models';

interface StageDefinition {
  stage: string;
  label: string;
  games: number;
}

// @TODO: usar enum
const stageDefinitions: StageDefinition[] = [
  {stage: 'final', label: 'Final', games: 1},
  {stage: 'semis', label: 'Semifinales', games: 2},
  {stage: 'quarters', label: 'Cuartos', games: 4},
  {stage: 'eighths', label: 'Octavos', games: 8},
  {stage: '_16th', label: '16avos', games: 16},
  {stage: '_32th', label: '32avos', games: 32},
  {stage: '_64th', label: '64avos', games: 64},
];

@Component({
  selector: 'draws-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './draws-ui.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class DrawsUi implements AfterViewInit {
  @ViewChild('brackets') brackets!: ElementRef;

  drawings: InputSignal<DrawingModel[]> = input.required<DrawingModel[]>();
  parentEventDrawings: InputSignal<DrawingModel[] | undefined> = input<DrawingModel[] | undefined>();
  games: InputSignal<GameModel[]> = input.required<GameModel[]>();
  isBackdraw: InputSignal<boolean> = input.required<boolean>();
  numberGroups: InputSignal<number | undefined> = input<number>();
  numbersQualifiersPerGroup: InputSignal<number | undefined> = input<number>();

  private stages: Signal<StageDefinition[]> = computed((): StageDefinition[] => {
    // Determina la ronda inicial
    let initialStagePos: number | undefined = undefined;
    const isBackdraw: boolean = this.isBackdraw();
    if (!isBackdraw) {
      // Cuadro A
      // Determina la ronda inicial basado en el numero de grupos y numero de clasificados por grupo

      // @TODO: tener en cuenta el numero de drawings que clasificaron de la fase de grupos. Puede darse el caso
      // de que el admin agregue mas jugadores adicionales a los que clasificaron de la fase de grupos.

      const numberGroups: number | undefined = this.numberGroups();
      const numbersQualifiersPerGroup: number | undefined = this.numbersQualifiersPerGroup();
      if (numberGroups !== undefined && numbersQualifiersPerGroup !== undefined) {
        initialStagePos = stageDefinitions.findIndex((item: StageDefinition): boolean => {
          return (item.games * 2) >= (numberGroups * numbersQualifiersPerGroup);
        });
      }
    } else {
      // Cuadro B
      // Determina la ronda inicial basado en el numero de jugadores que no clasifican al Cuadro A

      // @TODO: tener en cuenta el numero de drawings que clasificaron de la fase de grupos. Puede darse el caso
      // de que el admin agregue mas jugadores adicionales a los que clasificaron de la fase de grupos.

      const numberGroups: number | undefined = this.numberGroups();
      const numbersQualifiersPerGroup: number | undefined = this.numbersQualifiersPerGroup();
      if (numberGroups !== undefined && numbersQualifiersPerGroup !== undefined) {
        // Numero total de drawings en la fase de grupos (parent event)
        const parentEventDrawings: number = (this.parentEventDrawings() || []).length;
        initialStagePos = stageDefinitions.findIndex((item: StageDefinition): boolean => {
          return (item.games * 2) >= (parentEventDrawings - (numberGroups * numbersQualifiersPerGroup));
        });
      }
    }

    // Se determina la ronda inicial basado unicamente en el numero de drawings (unicos)
    // const initialStagePos: number = stageDefinitions.findIndex((item: StageDefinition): boolean => {
    //   console.info('aaa', item, this.drawings().length);
    //   return (item.games * 2) >= this.drawings().length;
    // });

    // Arma las definiciones de rondas basado en la ronda inicial
    const stages: StageDefinition[] = [];
    if (initialStagePos !== undefined && initialStagePos > -1) {
      for (let i = initialStagePos; i >= 0; i--) {
        stages.push(stageDefinitions[i]);
      }
    }

    return stages;
  });

  private rounds: Signal<{name: string}[]> = computed((): {name: string}[] => {
    const stages: StageDefinition[] = this.stages();

    return stages.map((item: StageDefinition): {name: string} => {
      return {name: item.label};
    });
  });

  private contestants: Signal<Params> = computed((): Params => {
    const drawings: DrawingModel[] = this.drawings();

    const contestants: Params = {};

    // Bye
    contestants['bye'] = {
      // entryStatus: '#22',
      players: [
        {
          _type: 'bye',
          title: 'Bye',
        },
      ],
    };

    // Posiciones abiertas
    contestants['p1'] = {
      // entryStatus: '#22',
      players: [
        {
          _type: 'generic',
          title: '(Jugador 1 - grupo 1)',
        },
      ],
    };
    contestants['p2'] = {
      // entryStatus: 'Q',
      players: [
        {
          _type: 'generic',
          title: '(Jugador 2 - grupo 1)',
        },
      ],
    };
    contestants['p3'] = {
      // entryStatus: 'Q',
      players: [
        {
          _type: 'generic',
          title: '(Jugador 1 - grupo 2)',
        },
      ],
    };
    contestants['p4'] = {
      // entryStatus: 'Q',
      players: [
        {
          _type: 'generic',
          title: '(Jugador 2 - grupo 2)',
        },
      ],
    };
    contestants['g14'] = {
      // entryStatus: 'Q',
      players: [
        {
          _type: 'generic',
          title: '(Ganador semifinal 1)',
        },
      ],
    };
    contestants['g23'] = {
      // entryStatus: 'Q',
      players: [
        {
          _type: 'generic',
          title: '(Ganador semifinal 2)',
        },
      ],
    };

    // Drawings clasificados a la fase de eliminacion
    for (const drawing of drawings) {
      // @TODO: deprecated
      // contestants[String(drawing.playerId)] = {
      contestants[String(drawing.teamId)] = {
        // entryStatus: 'Q',
        players: [
          {
            // @TODO: deprecated
            // title: drawing.player?.fullName,
            title: drawing.team?.label,
          },
        ],
      };
    }

    return contestants;
  });

  private matches = computed(() => {
    const stages: StageDefinition[] = this.stages();

    const matchesIndex: Params = {};

    // Partidos genericos
    let matchLabel: string;
    for (const i in stages) {
      for (let j = 0; j < stages[i].games; j++) {
        matchLabel = stages[i].label;
        // @TODO: usar enum
        if (stages[i].stage !== 'final') {
          matchLabel += ` ${j + 1}`;
        }
        matchesIndex[`${stages[i].stage}-${j}`] = {
          _matchLabel: matchLabel,
          roundIndex: +i,
          order: j,
          sides: [
            {
              // @TODO
              contestantId: 'p1',
            },
            {
              // @TODO
              contestantId: 'p4',
            },
          ],
        };
      }
    }


    // Arma las llaves parciales usando los registros Drawing (cuando las llaves
    // todavia no estan completas, por lo tanto, los partidos todavia no existen)
    const drawings: DrawingModel[] = this.drawings();
    for (const stage of stages) {
      const stageDrawings: DrawingModel[] = drawings.filter((model: DrawingModel): boolean => {
        return model.stage === stage.stage;
      });
      // @TODO: deprecated
      // let j: number;
      for (let i = 0; i < stage.games; i++) {
        // @TODO: deprecated
        // j = i * 2;
        const drawing1: DrawingModel | undefined = stageDrawings.find((model: DrawingModel): boolean => {
          // @TODO: deprecated
          // return model.sort === (j + 1);
          return model.group === (i + 1) && model.sort === 1;
        });
        if (drawing1) {
          // @TODO: deprecated
          // matchesIndex[`${stage.stage}-${i}`].sides[0].contestantId = String(drawing1.playerId);
          matchesIndex[`${stage.stage}-${i}`].sides[0].contestantId = String(drawing1.teamId);

          if (drawing1.bye) {
            matchesIndex[`${stage.stage}-${i}`].sides[1].contestantId = 'bye';
            // matchesIndex[`${stage.stage}-${i}`].sides[1].title = '<div style="margin-left: 60px;">BYE</div>';
            // matchesIndex[`${stage.stage}-${i}`].matchStatus = 'Bye';
          }
        }

        const drawing2: DrawingModel | undefined = stageDrawings.find((model: DrawingModel): boolean => {
          // @TODO: deprecated
          // return model.sort === (j + 2);
          return model.group === (i + 1) && model.sort === 2;
        });
        if (drawing2) {
          // @TODO: deprecated
          // matchesIndex[`${stage.stage}-${i}`].sides[1].contestantId = String(drawing2.playerId);
          matchesIndex[`${stage.stage}-${i}`].sides[1].contestantId = String(drawing2.teamId);

          if (drawing2.bye) {
            matchesIndex[`${stage.stage}-${i}`].sides[0].contestantId = 'bye';
            // matchesIndex[`${stage.stage}-${i}`].sides[0].title = '<div style="margin-left: 60px;">BYE</div>';
            // matchesIndex[`${stage.stage}-${i}`].matchStatus = 'Bye';
          }
        }
      }
    }

    // Partidos existentes
    const games: GameModel[] = this.games();
    for (const stage of stages) {
      const stageGames: GameModel[] = games.filter((model: GameModel): boolean => {
        return model.stage === stage.stage;
      });
      for (const model of stageGames) {
        matchesIndex[`${stage.stage}-${model.sort - 1}`]._matchCourt = model.court?.name;

        let matchDate: string | null = null;
        // @TODO: usar enum
        if (model.status === 'pending') {
          if (!model.date) {
            // @TODO
          } else {
            matchDate = this.datePipe.transform(model.date, 'dd/MM/yyy h:mm a');
          }
        } else {
          if (!model.startDate) {
            // @TODO
          } else {
            matchDate = this.datePipe.transform(model.startDate, 'dd/MM/yyy h:mm a');
          }
        }
        matchesIndex[`${stage.stage}-${model.sort - 1}`]._matchDate = matchDate;

        // @TODO: usar enum
        if (model.status == 'in_progress') {
          // @TODO
        } else if (model.status == 'finished') {
          if (!model.startDate || !model.endDate) {
            // @TODO
          } else {
            matchesIndex[`${stage.stage}-${model.sort - 1}`]._matchDuration = model.duration;
          }
        }

        // @TODO: usar enum
        if (model.status == 'in_progress') {
          matchesIndex[`${stage.stage}-${model.sort - 1}`].isLive = true;
        } else {
          matchesIndex[`${stage.stage}-${model.sort - 1}`].isLive = false;
        }
        // @TODO: usar enum
        if (model.status == 'finished') {
          if (model.winner === 1) {
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[0].isWinner = true;
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[1].isWinner = false;
          } else if (model.winner === 2) {
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[0].isWinner = false;
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[1].isWinner = true;
          } else {
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[0].isWinner = false;
            matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[1].isWinner = false;
          }
        }
        matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[0].scores = (model.scores || []).map((score: ScoreModel, index: number, scores: ScoreModel[]): Params => {
          const scoreOptions: Params = {
            mainScore: (score.score1 !== undefined) ? score.score1 : '-',
            subscore: score.extrascore1,
          };
          // @TODO: usar enum
          if (model.status == 'in_progress') {
            if (index < (scores.length - 1)) {
              scoreOptions['isWinner'] = (score.winner === 1);
            } else {
              scoreOptions['isWinner'] = true;
            }
          } else if (model.status == 'finished') {
            scoreOptions['isWinner'] = (score.winner === 1);
          }
          return scoreOptions;
        });
        matchesIndex[`${stage.stage}-${model.sort - 1}`].sides[1].scores = (model.scores || []).map((score: ScoreModel, index: number, scores: ScoreModel[]): Params => {
          const scoreOptions: Params = {
            mainScore: (score.score2 !== undefined) ? score.score2 : '-',
            subscore: score.extrascore2,
          };
          // @TODO: usar enum
          if (model.status == 'in_progress') {
            if (index < (scores.length - 1)) {
              scoreOptions['isWinner'] = (score.winner === 1);
            } else {
              scoreOptions['isWinner'] = true;
            }
          } else if (model.status == 'finished') {
            scoreOptions['isWinner'] = (score.winner === 2);
          }
          return scoreOptions;
        });
      }
    }

    return Object.values(matchesIndex);
  });

  private bracketsRef!: any;

  constructor(private datePipe: DatePipe) {
    effect((): void => {
      if (!this.bracketsRef) {
        console.info('brackets effect skip');
      } else {
        const data = {
          rounds: this.rounds(),
          contestants: this.contestants(),
          matches: this.matches(),
        };

        console.info('brackets effect data', data);

        this.bracketsRef.replaceData(data);
      }
    });
  }

  ngAfterViewInit(): void {
    // console.log('BracketsUiComponent ngAfterViewInit', this.brackets.nativeElement.innerHTML);

    const options: OptionsMap = {
      width: '100%',
      matchMinVerticalGap: 65,
      roundTitlesFontSize: 16,

      // @TODO: descomentar
      // getRoundTitleElement: (roundData, roundIndex: number): Element => {
      //   let pointsText = '';
      //   // if (roundIndex >= 0 && roundIndex <= 1) {
      //   //   let points: number | undefined;
      //   //   if (roundIndex === 0) {
      //   //     points = 150;
      //   //   } else if (roundIndex === 1) {
      //   //     points = 250;
      //   //   }
      //   //   if (points) {
      //   //     pointsText = `<div style="text-align: center; font-size: 14px; background: #fff6aa; padding: 2px 4px;">Puntos: ${points}</div>`;
      //   //   }
      //   // } else if (roundIndex === 2) {
      //   //   pointsText = '<div style="text-align: center; font-size: 14px; background: #fff6aa; padding: 2px 4px;">Puntos: 500 y 350</div>';
      //   // }
      //   if (roundIndex === 0) {
      //     pointsText = `<div style="text-align: center; font-size: 14px; background: #fff6aa; padding: 2px 4px;">Puntos: 50</div>`;
      //   } else if (roundIndex === 1) {
      //     pointsText = '<div style="text-align: center; font-size: 14px; background: #fff6aa; padding: 2px 4px;">Puntos: 150 y 100</div>';
      //   }
      //
      //   const div = document.createElement('div');
      //   div.innerHTML = `<div>
      //     <div style="text-align: center; font-size: 16px;">${roundData.name}</div>
      //     ${pointsText}
      //   </div>`;
      //   // Change this to div.childNodes to support multiple top-level nodes
      //   return div.firstElementChild as Element;
      // },

      // getMatchElement: (roundIndex, matchOrder) => { /* ...return an Element for a whole match OR null */ },
      //
      // getEntryStatusHTML: (entryStatus, context) => { /* ...return an HTML string for contestant's entryStatus */ },
      getEntryStatusHTML: (entryStatus, context) => {
        // return `<div style="width: 24px;">${entryStatus}<div>`;
        if (!entryStatus) {
          return '';
        }
        return `<span style="background: pink; color: black; padding: 1px 3px;">${entryStatus}</span>`;
      },
      //
      // getNationalityHTML: (player, context) => { /* ...return an HTML string for player's nationality */ },

      // getPlayerTitleHTML: p => `<a href="http://google.com/search?q=${p.title}" target="blank">
      //       ${p.title || ''}
      //       <svg class="link-svg" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M13.723 18.654l-3.61 3.609c-2.316 2.315-6.063 2.315-8.378 0-1.12-1.118-1.735-2.606-1.735-4.188 0-1.582.615-3.07 1.734-4.189l4.866-4.865c2.355-2.355 6.114-2.262 8.377 0 .453.453.81.973 1.089 1.527l-1.593 1.592c-.18-.613-.5-1.189-.964-1.652-1.448-1.448-3.93-1.51-5.439-.001l-.001.002-4.867 4.865c-1.5 1.499-1.5 3.941 0 5.44 1.517 1.517 3.958 1.488 5.442 0l2.425-2.424c.993.284 1.791.335 2.654.284zm.161-16.918l-3.574 3.576c.847-.05 1.655 0 2.653.283l2.393-2.389c1.498-1.502 3.94-1.5 5.44-.001 1.517 1.518 1.486 3.959 0 5.442l-4.831 4.831-.003.002c-1.438 1.437-3.886 1.552-5.439-.002-.473-.474-.785-1.042-.956-1.643l-.084.068-1.517 1.515c.28.556.635 1.075 1.088 1.528 2.245 2.245 6.004 2.374 8.378 0l4.832-4.831c2.314-2.316 2.316-6.062-.001-8.377-2.317-2.321-6.067-2.313-8.379-.002z"/></svg>
      //   </a>`,
      getPlayerTitleHTML: (player, context): string => {
        let icon = '';
        if (context.roundIndex === 1 && ['46', '51'].includes(context.contestantId)) {
          icon = '<span class="material-symbols-outlined">trophy</span>';
        }
        let style: string;
        if (player._type === 'generic') {
          // style = ' style="color: #0A58CA; font-style: italic;"';
          style = ' style="color: #88a2bc; font-style: italic;"';
          // @TODO: borrar
          return '';
        } else if (player._type === 'bye') {
          return '<span style="color: #88a2bc;">Bye</span>';
        } else {
          style = '';
          // @TODO: borrar
          return `<span${style}>${player.title}${icon}</span>`;
        }
        // @TODO: descomentar
        // return `<span${style}>${player.title}${icon}</span>`;
      },

      // getScoresHTML: (side, match) => { /* ...return an HTML string for side's scores */ },

      getMatchTopHTML: (match): string => {
        console.info('getMatchTopHTML', match);
        return `<span style="background-color: #EAF2FE; color: black; padding: 1px 3px; border: #C4DBFD solid 1px;">${match._matchLabel}</span>`;
      },

      getMatchBottomHTML: (match): string => {
        console.info('getMatchBottomHTML', match);
        const htmlItems: string[] = [];

        if (match._matchCourt) {
          htmlItems.push(`<span style="color: black; padding: 1px 3px; border: #C4DBFD solid 1px;">${match._matchCourt}</span>`);
        }

        if (match._matchDate) {
          htmlItems.push(`<span style="color: black; padding: 1px 3px; border: #C4DBFD solid 1px;">${match._matchDate}</span>`);
        }

        if (match._matchDuration) {
          htmlItems.push(`<span style="color: black; padding: 1px 3px; border: #C4DBFD solid 1px;">${match._matchDuration}</span>`);
        }

        return htmlItems.join('');
      },
    };

    const data = {
      rounds: this.rounds(),
      contestants: this.contestants(),
      matches: this.matches(),
    };

    console.info('brackets ngAfterViewInit data', data);

    this.bracketsRef = createBracket(data, this.brackets.nativeElement, options);
  }
}
